import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Check if there's a stored token and verify it
        const token = localStorage.getItem('authToken');
        if (token) {
            verifyToken(token);
        } else {
            setLoading(false);
        }
    }, []);

    const verifyToken = async (token) => {
        try {
            const response = await fetch('/api/auth/verify', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUser({
                    ...data.data,
                    token,
                    must_change_password: data.data.must_change_password || false
                });
            } else if (response.status === 401) {
                // Token abgelaufen - versuche zu reaktivieren
                console.log('Token abgelaufen, versuche Reaktivierung...');
                const reactivateResponse = await fetch('/api/auth/reactivate', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (reactivateResponse.ok) {
                    // Token reaktiviert - erneut verifizieren
                    console.log('Token erfolgreich reaktiviert');
                    const verifyAgain = await fetch('/api/auth/verify', {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (verifyAgain.ok) {
                        const data = await verifyAgain.json();
                        setUser({
                            ...data.data,
                            token
                        });
                    } else {
                        localStorage.removeItem('authToken');
                    }
                } else {
                    // Token konnte nicht reaktiviert werden
                    localStorage.removeItem('authToken');
                }
            } else {
                // Anderer Fehler
                localStorage.removeItem('authToken');
            }
        } catch (error) {
            console.error('Token verification failed:', error);
            localStorage.removeItem('authToken');
        }
        setLoading(false);
    };

    const loginAdmin = async (username, password) => {
        const response = await fetch('/api/auth/admin/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Login fehlgeschlagen');
        }

        localStorage.setItem('authToken', data.data.token);
        setUser({
            ...data.data,
            token: data.data.token,
            must_change_password: data.data.must_change_password || false
        });

        return data.data;
    };

    const refreshUser = async () => {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const response = await fetch('/api/auth/verify', {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
            const data = await response.json();
            setUser({ ...data.data, token });
        }
    };

    const loginMember = async (username, password) => {
        const response = await fetch('/api/auth/member/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password })
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Login fehlgeschlagen');
        }

        localStorage.setItem('authToken', data.data.token);
        setUser({
            ...data.data,
            token: data.data.token
        });

        return data.data;
    };

    const logout = async () => {
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
            } catch (error) {
                console.error('Logout error:', error);
            }
        }
        localStorage.removeItem('authToken');
        setUser(null);
    };

    const changePassword = async (currentPassword, newPassword) => {
        const token = localStorage.getItem('authToken');
        const body = { newPassword };
        if (currentPassword) body.currentPassword = currentPassword;
        const response = await fetch('/api/auth/admin/change-password', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(body)
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Passwort aendern fehlgeschlagen');
        }

        // must_change_password Flag im User-State zuruecksetzen
        setUser(prev => prev ? { ...prev, must_change_password: false } : prev);

        return true;
    };

    // Wrapper fuer fetch mit automatischem Logout bei 401
    const authFetch = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        const headers = {
            ...options.headers,
            'Authorization': `Bearer ${token}`
        };

        if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(options.body);
        }

        const response = await fetch(url, { ...options, headers });

        // Bei 401 automatisch ausloggen
        if (response.status === 401) {
            console.log('Session abgelaufen - automatischer Logout');
            localStorage.removeItem('authToken');
            setUser(null);
            window.location.href = '/login?expired=1';
            throw new Error('Session abgelaufen. Bitte erneut einloggen.');
        }

        return response;
    };

    const value = {
        user,
        loading,
        loginAdmin,
        loginMember,
        logout,
        changePassword,
        refreshUser,
        authFetch,
        isAdmin: user?.userType === 'admin',
        isMember: user?.userType === 'member',
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
